/**
 * NavRailButton — one entry of the nav rail: a toggle for the panel that it opens, or an action
 * on the stage. Collapsed, it shows only its icon and names itself in a tooltip. Expanded, it
 * shows its label beside the icon, and the label is the name.
 *
 * The styles are plain CSS in navRailButton.css, which NavRailBadge shares, so the buttons and
 * the badges of the rail keep one shape.
 */

import './navRailButton.css';

import type { ComponentType, ReactElement } from 'react';

import { Hint } from '../Hint';

import { AttentionDot } from './AttentionDot';

interface Props {
    /** The glyph of the entry; `null` for a panel that declares none. */
    icon: ComponentType<{ className?: string }> | null;
    label: string;
    /** The panel of the button is open. Omitted for an action, which opens no panel. */
    isPressed?: boolean;
    /** The rail is wide enough to show labels. */
    isExpanded: boolean;
    /** A row sized for a finger rather than a pointer. */
    isRoomy?: boolean;
    hasDot?: boolean;
    onClick: () => void;
}

export const NavRailButton = ({
    icon: Icon, label, isPressed, isExpanded, isRoomy = false, hasDot = false, onClick,
}: Props): ReactElement => (
    <Hint
        label={isExpanded ? undefined : label}
        side="right"
    >
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            aria-description={hasDot ? `${label} needs your attention` : undefined}
            aria-pressed={isPressed}
            data-action={isPressed === undefined || undefined}
            data-expanded={isExpanded || undefined}
            data-roomy={isRoomy || undefined}
            className="occ-nav-button relative"
        >
            {Icon && <Icon className="occ-nav-button-icon" />}
            {isExpanded && <span className="occ-nav-button-label">{label}</span>}
            {hasDot && (
                <AttentionDot className={isExpanded
                    ? 'relative -top-1.5 -left-1'
                    : 'absolute top-[calc(50%-12px+0.2em)] left-[calc(50%+4px+0.2em)]'}
                />
            )}
        </button>
    </Hint>
);
